import { TZDate } from '@toast-ui/calendar';
import State from './State.tsx';
import Task from '../../../../models/task.tsx';
import User from '../../../../models/user.tsx';
import Location from '../../../../models/location.tsx';
import Role from '../../../../models/role.tsx';
import TypeAction from '../../../../models/typeAction.tsx';
import React, { ReactElement } from 'react';
import Ability from '../../../../models/ability.tsx';

class ObjectEvent { 
  id: string;                                  
  calendarId: string;                          
  title: string;                               
  category: string;                            
  location: Location;                          
  backgroundColor: string;                     
  attendees: { roles: Role[], users: User[] }; 
  start: TZDate;                               
  end: TZDate;                                 
  state: string;                               
  raw: { id_plannings: string };               
  stacked_task?: boolean = false;
}

class ICalendarFactory {

  static levelColor(value: number): string {
    switch (value) {
      case 1: return '#FF5555';
      case 2: return '#FF9944';
      case 3: return '#33BBDD';
      case 4: return '#44BB44';
      default: return '#';
    }
  }

  /** Converts TZDate to hh-mm */
  static TZDateHourFormat(date: TZDate): string {
    let hour: string = date.getHours();
    let minutes: string = date.getMinutes();

    if (String(hour).length < 2) hour = '0' + hour;
    if (String(minutes).length < 2) minutes = '0' + minutes;

    return `${hour}:${minutes}`;
  }

  /** Get yesterday as an index of the week ( 0 to 5 ) */
  static getYersterday(): number {
    let day = new Date().getDay() - 1;
    let yesterday = day % 5;
    if (yesterday <= 0) yesterday = 1;

    return yesterday;
  }

  /** Converts TZDate to YYYY-MM-DD */
  static TZDateFormat(date: TZDate): string {
    let year: string = date.getFullYear();
    let month: string = date.getMonth() + 1;
    let day: string = date.getDate();

    if (String(month).length < 2) month = '0' + month;
    if (String(day).length < 2) day = '0' + day;

    return `${year}-${month}-${day}`;
  }


  static firstLetterUpperCase(str: string): string {
    str = str.toLowerCase();
    str = str[0].toUpperCase() + str.slice(1);
    return str;
  }


  static objectEventToTask(event: ObjectEvent): Task {
    console.log(event);

    return new Task(
      this.TZDateFormat(event.start),
      null,
      0,
      this.TZDateHourFormat(event.end),
      this.TZDateHourFormat(event.start),
      event.id,
      event.location.id_location,
      event.raw.id_plannings,
      null,
      event.title.id_type_action,
      0,
      0,
      event.state,
      event.attendees.rolesTask,
      event.attendees.users,
      event.attendees.rolesTask
    );
  }

  /** Sort an array of Task by hour. *to put it in a grid* */
  static sortTasks(tasks: ObjectEvent[]): ObjectEvent[] {

    // sort tasks by start hour and minutes
    tasks.sort((a, b) => {
      if (parseInt(a.start.getHours()) < parseInt(b.start.getHours())) {
        return -1;
      }
      if (parseInt(a.start.getHours()) === parseInt(b.start.getHours())) {
        if (parseInt(a.start.getMinutes()) < parseInt(b.start.getMinutes())) {
          return -1;
        }
      }
      return 0;
    });


    let lines: Array<{ value: number; tasks: ObjectEvent[] }> = [];

    for (let index in tasks) {
      let start_m = parseInt(tasks[index].start.getMinutes());
      let end_m = parseInt(tasks[index].end.getMinutes());

      // to convert minutes on 60 to decimal on 100;
      const val = (100 / 60);

      start_m = start_m * val / 100;
      end_m = end_m * val / 100;

      const start_h = parseInt(tasks[index].start.getHours()) + start_m;
      const end_h = parseInt(tasks[index].end.getHours()) + end_m;

      // init lines tab
      if (lines[0] == null) {
        lines.push({
          value: start_h,
          tasks: []
        });
      }

      // apply the sort algo
      let check = false;
      for (let line of lines) {
        if (line.value <= start_h) {
          line.value = end_h;
          line.tasks.push(tasks[index]);
          check = true;
          break;
        }
      }
      if (!check) {
        lines.push({
          value: end_h,
          tasks: [tasks[index]]
        });
      }
    }

    // make the new tab with the sorted lines
    let res_tasks: ObjectEvent[] = [];

    for (let index = 0; index < lines.length; index++) {
      const stacked = index !== 0;
      for (let event of lines[index].tasks) {
        event.stacked_task = stacked;
        res_tasks.push(event);
      }
    }

    return res_tasks;
  }

  /** Sort an array of Task by date. *to put it in a grid* */
  static sortTasksByDate(tasks: ObjectEvent[]): ObjectEvent[] {

    // sort tasks by start hour and minutes
    tasks.sort((a, b) => {
      if (parseInt(a.start.getHours()) < parseInt(b.start.getHours())) {
        return -1;
      }
      if (parseInt(a.start.getHours()) === parseInt(b.start.getHours())) {
        if (parseInt(a.start.getMinutes()) < parseInt(b.start.getMinutes())) {
          return -1;
        }
      }
      return 0;
    });

    // put each task in his day column
    let days: ObjectEvent[][] = [];
    for (let task of tasks) {
      let day: number = task.start.getDate();
      if (days[day] == null)
        days[day] = [];

      days[day].push(task);
    }

    // make the new tab with the sorted lines
    let res_tasks: ObjectEvent[] = [];

    let emptyDays = 0;
    while (emptyDays < days.length) {
      days.forEach(day => {
        if (day != null && day.length > 0) {
          res_tasks.push({ ...day[0] });
          day.shift();
        }
        else emptyDays++;
      })
    }

    return res_tasks;
  }

  static taskToObjectEvent(
    json: Task,
    view,
    listRole: Role[],
    listLocation: Location[],
    listTypeAction: TypeAction[],
    listRolesTasks: []
  ) {

    let skills: Role[] = [];
    let abilities: Ability[] = [];
    let rolesTask = json.rolesTasks;

    //if rolesTask is not define, search from DB
    if (rolesTask === undefined) {
      rolesTask = listRolesTasks?.filter(rt => rt.id_task === json.id_case_plannings);

      if (rolesTask != null && rolesTask.length > 0) {
        rolesTask.forEach(rt => {
          skills = rt.skills !== undefined ? [...rt.skills] : [];
          skills.forEach(s => {
            let skill = listRole.find(r => r.id_role === s.id_skill);
            if (skill != null) {
              s.name = skill.name;
              s.color = skill.color;
            }
          })
          rt.skills = skills;
        })


        //TODO !!!
        //abilities = [...rolesTask.abilities]
        /* 
        abilities.map( a => {
          let ability = listAbilites.find(r => r.id_role === a.id_ability);
          if (ability != null) {
            a.name = ability.name;
            a.color = ability.color;
          }
        }) */
        //users = [...rolesTask.users]
      }
    }

    let location: Location;
    let typeAction: TypeAction;

    // recover location
    if (listLocation.length > 0) {
      for (let locationObj of listLocation) {
        if (locationObj.id_location === json.id_location) {
          location = { ...locationObj };
        }
      }
    }

    // recover typeAction
    if (listTypeAction.length > 0) {
      for (let typeActionObj of listTypeAction) {
        if (typeActionObj.id_type_action === json.id_type_action) {
          typeAction = typeActionObj;
        }
      }
    }

    const attendees = {
      rolesTask: rolesTask
    }
    let res = {
      id: json.id_case_plannings,
      calendarId: typeAction?.id_type_action, // TODO weird here
      title: typeAction,
      category: view,
      location: location,
      backgroundColor: State.statusToColor(json.status),
      attendees: attendees,
      start: new TZDate(json.date + 'T' + json.hourly_start),
      end: new TZDate(json.date + 'T' + json.hourly_end),
      state: json.status,
      raw: { id_plannings: json.id_plannings },
      borderColor: '#f005'
    };
    //console.log(res)
    return res;
  }

  static extractTask(tasks, view, listRole, listLocation, listTypeAction, listUser, listRolesTasks, condition) {
    let events = [];

    if (condition) {
      for (let task of tasks) {
        if (condition(task)) {
          events.push(this.taskToObjectEvent(task, view, listRole, listLocation, listTypeAction, listUser, listRolesTasks));
        }
      }
    }
    else {
      for (let task of tasks) {
        events.push(this.taskToObjectEvent(task, view, listRole, listLocation, listTypeAction, listUser, listRolesTasks));
      }
    }

    return events;
  }

  static hoursLenghtByRange(range: { start: number, end: number }, step: number = 1): number {
    return this.hoursByRange(range, step).length;
  }

  static hoursByRange(range: { start: number, end: number }, step: number = 1): number[] {
    if (range.start >= range.end) return [0];

    const hours: number[] = [];
    for (let hour = range.start; hour < range.end; hour += step) {
      hours.push(hour);
    }

    return hours;
  }

  static positiveModulo(val: number, modulo: number): number {
    return (((val) % modulo) + modulo) % modulo;
  }


  static millisToHour(val: number): string {
    val = val / 1000 / 3600;
    return this.decimalToHour(val);
  }

  static decimalToHour(val: number): string {
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 60);

    if (minutes >= 60)
      return `${(hours + 1) < 10 ? '0' : ''}${hours + 1}:00`;

    return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
  }

  static decimalToHourColapsed(val: number): string {
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 60);

    if (minutes >= 60) return `${hours + 1}h`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes < 10 ? '0' : ''}${minutes}`;
  }

  static HourToDecimal(hour: string): number {
    const splited = hour.split(':');
    let hours = parseInt(splited[0]);
    let minutes = parseInt(splited[1]);
    minutes = Math.round(((minutes / 60) + Number.EPSILON) * 100) / 100

    return hours + minutes;
  }

  static getWeek = (date: Date): number => {
    const firstMonday = this.getMonday(new Date(date.getFullYear(), 0, 1));

    // 7 * 24 * 3600 * 1000 = 604800000 = a week in ms
    return Math.floor((date.getTime() - firstMonday.getTime()) / (604800000));
  };

  /** function to test the function getWeek() by brut force */
  static testGetWeek(): ReactElement<any, any> {
    const res: string[][] = [];
    for (let j = 0; j < 20; j++) {
      let r: string[] = []
      let day = new Date(2024 + j, 0, 1);

      for (let i = 0; i < 365; i++) {
        r.push(this.TZDateFormat(new TZDate(day)) + ' -> ' + this.getWeek(day) + '; ' + (this.getMonday(day).getDay() === day.getDay() ? 'monday' : ''))
        day = new Date(day.getTime() + 24 * 3600 * 1000);
      }
      res.push(r);
    }

    let final = (<div className="text-xs flex flex-wrap w-fit debug">{res.map(line => (
      <div className="mx-1">{line.map(y => (
        <div>{y}</div>
      ))}</div>
    ))}</div>);

    return final;
  }

  /**
   * Check if a User can get a RoleTask
   * @param rt RoleTask
   * @param user the user to check
   */
  static compareRolesTasks = (
    role_task: {
      users: []
      abilities: []
      skills: []
    },
    user: {
      id: string
      abilities: []
      skills: []
    }
  ) => {

    // the user is already linked to this RoleTask
    if (role_task.users.some(u => u.id_user === user.id)) return true;

    if (ICalendarFactory.compareRolesTasksWithAbilitiesAndSkills(role_task, user.abilities, user.skills)) {
      return true;
    }
    return false;
  }

  /**
 * Check if abilities and skills can get a RoleTask
 * @param role_task RoleTask
 * @param abilities the abilities to check
 * @param skills the skills to check
 */
  static compareRolesTasksWithAbilitiesAndSkills = (
    role_task: {
      users: []
      abilities: []
      skills: []
    },
    abilities: [],
    skills: []
  ) => {

    if (
      (role_task.abilities.length <= 0 || role_task.abilities.every(a => abilities.some(a2 => a2.id_ability === a.id_ability))) &&
      (role_task.skills.length <= 0 || role_task.skills.every(r => skills.some(
        r2 => {
          let id = r2.id_skill == null ? r2.id_role : r2.id_skill;
          return ((id === r.id_role || id === r.id_skill) && r2.iluo >= r.iluo)
        }
      )))
    ) {
      return true;
    }
    return false;
  }

  static getMonday(d: Date): Date {
    d = new Date(d);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    let monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  }


  static generateHashFromArray(arr: (number | string | boolean)[], str?: string, strs?: string[]): string {
    let res = arr.reduce((hash, item) => {
      const str = item ? item.toString() : '0';
      let value = 0;
      for (let i = 0; i < str.length; i++) {
        value = (hash << 5) - hash + str.charCodeAt(i);
        value |= 0; // Convert to 32-bit integer
      }
      return value;
    }, 0);
    return (res.toString() + (str ? str : '') + strs?.map(s => s));
  }
}


export default ICalendarFactory;

